import { HttpErrorResponse } from '@angular/common/http';

export interface DescribedError {
  /** One-line headline naming the step that failed. */
  summary: string;
  /** Fact lines: status, URL, server message, debugId, etc. */
  detail: string[];
  /** What to actually do about it, when we can tell. */
  hint?: string;
}

/** Shape our lambdas return via retError(): { message, details: { debugId } }. */
interface ApiErrorBody {
  message?: string;
  details?: { debugId?: string; errors?: unknown };
}

function describeHttp(e: HttpErrorResponse, step: string): DescribedError {
  const detail: string[] = [];
  const body: ApiErrorBody | string | null = e.error ?? null;

  // status 0 means the request never got a usable HTTP response at all.
  if (e.status === 0) {
    detail.push(`Endpoint: ${e.url ?? 'unknown'}`);
    detail.push('No HTTP response — the request never reached the API.');
    return {
      summary: `Could not reach the API while trying to ${step}.`,
      detail,
      hint:
        'The lambda is probably not deployed yet, the host is unreachable, or CORS blocked the ' +
        'request. Check the Network tab for the failed request and confirm the endpoint exists.',
    };
  }

  detail.push(`HTTP ${e.status}${e.statusText ? ' ' + e.statusText : ''}`);
  if (e.url) detail.push(`Endpoint: ${e.url}`);

  if (typeof body === 'string' && body.trim()) {
    detail.push(`Server said: ${body}`);
  } else if (body && typeof body === 'object') {
    if (body.message) detail.push(`Server said: ${body.message}`);
    if (body.details?.debugId) detail.push(`debugId: ${body.details.debugId}`);
    if (body.details?.errors) {
      detail.push(`Validation: ${JSON.stringify(body.details.errors)}`);
    }
  }

  let hint: string | undefined;
  if (e.status === 404) {
    hint = 'That endpoint does not exist — the lambda is very likely not deployed to this stage.';
  } else if (e.status === 500) {
    hint =
      'Server-side misconfiguration. For the credentials step this usually means ' +
      'RECOVERY_LIVENESS_ROLE_ARN is unset — the terraform IAM role has not been applied yet.';
  } else if (e.status === 502) {
    hint =
      'The lambda reached Rekognition but the call failed. Check that Face Liveness is enabled ' +
      'in us-east-1 and that the execution role has the rekognition:* permissions.';
  } else if (e.status === 400) {
    hint = 'The request was rejected as malformed or blocked by the suspicious-pattern guard.';
  } else if (e.status === 403) {
    hint = 'Permission denied — check the lambda execution role policy.';
  }

  return { summary: `Failed to ${step}.`, detail, hint };
}

/** Duck-typed LivenessError from the Amplify widget: { state, error }. */
function asLivenessError(e: unknown): { state: string; error?: Error } | null {
  if (!e || typeof e !== 'object') return null;
  const c = e as { state?: unknown; error?: unknown };
  if (typeof c.state !== 'string') return null;
  return { state: c.state, error: c.error instanceof Error ? c.error : undefined };
}

const LIVENESS_HINTS: Record<string, string> = {
  CAMERA_ACCESS_ERROR: 'The browser blocked camera access. Allow the camera and retry.',
  CAMERA_FRAMERATE_ERROR: 'The camera framerate is too low for the liveness check.',
  FRESHNESS_TIMEOUT: 'The check timed out. Ensure the face is centred and well lit, then retry.',
  SERVER_ERROR:
    'Rekognition rejected the stream. Common causes: the session id was already used, it ' +
    'expired, or the browser credentials lack rekognition:StartFaceLivenessSession.',
  TIMEOUT: 'The liveness session timed out before completing.',
  RUNTIME_ERROR: 'The widget hit a runtime error — see the browser console for the stack.',
};

/**
 * Turn anything thrown during the recovery flow into something a human can act on.
 * `step` is a verb phrase, e.g. "mint a liveness session".
 */
export function describeError(e: unknown, step: string): DescribedError {
  if (e instanceof HttpErrorResponse) return describeHttp(e, step);

  const live = asLivenessError(e);
  if (live) {
    const detail = [`Liveness error state: ${live.state}`];
    if (live.error?.message) detail.push(`Underlying: ${live.error.message}`);
    return {
      summary: `The liveness check failed while trying to ${step}.`,
      detail,
      hint: LIVENESS_HINTS[live.state],
    };
  }

  if (e instanceof Error) {
    return {
      summary: `Failed to ${step}.`,
      detail: [`${e.name}: ${e.message}`],
    };
  }

  return {
    summary: `Failed to ${step}.`,
    detail: [`Unrecognised error value: ${safeStringify(e)}`],
  };
}

function safeStringify(v: unknown): string {
  try {
    return typeof v === 'string' ? v : JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}
